import { getLocaleFromLanguage } from '@seaters/constants';
import {
  queryClient,
  useFetchGuestFanGroup,
  useFetchGuestWaitingList,
} from '@seaters/data-access';
import {
  lightTheme,
  Loader,
  primaryColor,
  PrototypeModeIndicator,
} from '@seaters/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  Alert,
  ConfigProvider,
  Image,
  Layout,
  Result,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import * as Sentry from '@sentry/react';

import 'dayjs/locale/nl';
import 'dayjs/locale/fr';

import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { EventInformationCard } from './EventInformationCard';
import { ExperienceCard } from './ExperienceCard';
import { InvalidLink } from './InvalidLink';
import { IxpoleGuestFlow } from './IxpoleGuestFlow';
import { TicketsSelectCard } from './TicketsSelectCard';

import { TitleCard } from './TitleCard';
import { getBrandColors } from './utils';

import { useTranslation } from 'react-i18next';

import '../i18n/config';
import './app.module.css';
import { useEffect } from 'react';

dayjs.extend(LocalizedFormat);

const { Header, Footer, Content } = Layout;

const { Paragraph } = Typography;

const headerStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  color: '#fff',
  height: 70,
  paddingInline: '40px',
};

const contentStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  color: '#fff',
  paddingBottom: 20,
  width: '100%',
};

const footerStyle: React.CSSProperties = {
  color: '#fff',
};

const layoutStyle = {
  maxWidth: 800,
  minHeight: '100vh',
  overflow: 'hidden',
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Invitation />
      <PrototypeModeIndicator />
    </QueryClientProvider>
  );
}

function Invitation() {
  const { i18n, t } = useTranslation();
  const searchParams = new URLSearchParams(window.location.search);

  const slug = searchParams.get('fanGroupSlug') || '';
  const waitingListId = searchParams.get('waitingListId') || '';
  const code = searchParams.get('code') || '';

  const lang = searchParams.get('lang') || 'en';

  useEffect(() => {
    // The language that actually loaded: an unsupported `lang` falls back to English.
    i18n.changeLanguage(lang).then(() => {
      document.documentElement.lang = getLocaleFromLanguage(
        i18n.resolvedLanguage ?? 'en'
      );
    });
  }, [i18n, lang]);

  // Every field is required: the app is query-param driven with no router, so a URL
  // missing any of them carries no invitation at all. Distinguishing that from "still
  // loading" is what stops the bare root rendering a spinner forever.
  const hasInvitationParams = Boolean(slug && waitingListId && code);

  const { data: fanGroupData, isError: fanGroupFailed } = useFetchGuestFanGroup(
    slug,
    { code },
    { enabled: hasInvitationParams }
  );
  const { data: waitingListData, isError: waitingListFailed } =
    useFetchGuestWaitingList(
      waitingListId,
      { code },
      { enabled: hasInvitationParams }
    );

  // A rejected code — expired, revoked, already consumed — is as unusable as a
  // missing one, so both land on the same dead end rather than on a spinner.
  // Each failure is paired with its own `data` check: `keepPreviousData` keeps a
  // successful payload while a later refetch is failing, and tearing a working RSVP
  // page down to a dead end over a transient blip would be worse than the spinner.
  const invitationUnavailable =
    !hasInvitationParams ||
    (fanGroupFailed && !fanGroupData) ||
    (waitingListFailed && !waitingListData);

  const experienceName =
    waitingListData?.translatedExperienceName ||
    waitingListData?.translatedEventName;

  const pageTitle = invitationUnavailable
    ? t('guest_app_invalid_link_title')
    : experienceName &&
      [experienceName, fanGroupData?.translatedName]
        .filter(Boolean)
        .join(' | ');

  useEffect(() => {
    if (pageTitle) document.title = pageTitle;
  }, [pageTitle]);

  dayjs.locale(lang);

  const brandColors = getBrandColors(fanGroupData?.color, primaryColor);

  const theme =
    fanGroupData && fanGroupData.color
      ? {
          // Extend lightTheme: it carries the AA text tokens (secondary text, links).
          ...lightTheme,
          token: { ...lightTheme.token, colorPrimary: brandColors.brand },
          components: {
            Button: { primaryColor: brandColors.onBrand },
          },
        }
      : lightTheme;

  const hasSeat = waitingListData?.position?.status === 'HAS_SEAT';
  const hasPosition = waitingListData?.position;

  return (
    <Sentry.ErrorBoundary
      fallback={
        <main>
          <Result status="error" title={t('guest_app_error_fallback')} />
        </main>
      }
    >
      <ConfigProvider theme={theme}>
        <Layout style={layoutStyle}>
          {invitationUnavailable ? (
            <InvalidLink />
          ) : fanGroupData && waitingListData ? (
            waitingListData.ixpoleWaitingList ? (
              <Content
                style={{
                  ...contentStyle,
                  paddingBottom: 0,
                }}
              >
                {hasPosition && hasSeat ? (
                  <IxpoleGuestFlow
                    waitingListData={waitingListData}
                    fanGroupData={fanGroupData}
                  />
                ) : (
                  <Alert
                    message={t('guest_app_waitingSeat')}
                    type="info"
                    showIcon
                  />
                )}
              </Content>
            ) : (
              <>
                <Header style={headerStyle}>
                  <Image
                    src={fanGroupData.profileImageUrl}
                    alt={fanGroupData.translatedName}
                    height={'100%'}
                    preview={false}
                    style={{ padding: '4px 0' }}
                  />
                </Header>
                <Content style={contentStyle}>
                  <Image
                    src={fanGroupData.coverImageUrl}
                    alt=""
                    height={'20%'}
                    style={{
                      marginBottom: -58,
                    }}
                    preview={false}
                  />
                  <Space
                    direction="vertical"
                    size={20}
                    style={{ padding: '0 10%', width: '100%' }}
                  >
                    <TitleCard
                      waitingListData={waitingListData}
                      fanGroupData={fanGroupData}
                    />
                    {hasPosition && hasSeat ? (
                      <TicketsSelectCard fanGroupData={fanGroupData} />
                    ) : (
                      <Alert
                        message={t('guest_app_waitingSeat')}
                        type="info"
                        showIcon
                      />
                    )}

                    <ExperienceCard
                      waitingListData={waitingListData}
                      fanGroupData={fanGroupData}
                    />
                    <EventInformationCard
                      waitingListData={waitingListData}
                      fanGroupData={fanGroupData}
                    />
                  </Space>
                </Content>
                <Footer
                  style={{
                    ...footerStyle,
                    backgroundColor: brandColors.brand,
                  }}
                >
                  <ConfigProvider
                    theme={{
                      token: {
                        colorText: brandColors.onBrand,
                      },
                    }}
                  >
                    <Paragraph strong>{t('guest_app_question')}</Paragraph>
                    <Paragraph strong>
                      {t('guest_app_contact')}{' '}
                      <a
                        href={
                          fanGroupData?.helpLinks?.[lang] ||
                          t('helpdesk_url') ||
                          ''
                        }
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: brandColors.onBrand,
                          textDecoration: 'underline',
                        }}
                      >
                        {t('guest_app_contact_link')}
                      </a>
                    </Paragraph>
                    <Paragraph
                      strong
                      style={{ textAlign: 'center', fontSize: 12 }}
                    >
                      {t('footer_dynamic_copyright', {
                        currentYear: dayjs(new Date()).format('YYYY'),
                      })}
                    </Paragraph>
                  </ConfigProvider>
                </Footer>
              </>
            )
          ) : (
            <Loader show />
          )}
        </Layout>
        <div />
      </ConfigProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
